import { useState } from "react";
import type { Member } from "../../../../services/types";
import { PROOF_LABEL } from "../../../../config/members";
import { Card } from "../../../../components/base/Card";

function InfoRow({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-foreground-500">{label}</dt>
      <dd className="mt-1 break-words text-sm text-foreground-900">{value || "—"}</dd>
    </div>
  );
}

function maskProof(value: string): string {
  if (value.length <= 4) return "••••";
  return `${"•".repeat(Math.max(4, value.length - 4))}${value.slice(-4)}`;
}

export function OverviewTab({ member }: { member: Member }) {
  const [revealProof, setRevealProof] = useState(false);

  return (
    <div className="space-y-4">
      <Card title="Identity & contact">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <InfoRow label="Full name" value={member.fullName} />
          <InfoRow label="Father's / Spouse's name" value={member.guardianName} />
          <InfoRow label="Primary phone" value={member.primaryPhone} />
          <InfoRow label="Alternate phone" value={member.alternatePhone} />
          <InfoRow label="Email" value={member.email} />
          <InfoRow label="Member code" value={member.memberCode} />
        </dl>
      </Card>

      <Card title="Address">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <InfoRow label="Address line 1" value={member.addressLine1} />
          <InfoRow label="Address line 2" value={member.addressLine2} />
          <InfoRow label="City" value={member.city} />
          <InfoRow label="State" value={member.state} />
          <InfoRow label="PIN code" value={member.pinCode} />
        </dl>
      </Card>

      <Card title="Identity proof">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <InfoRow label="Proof type" value={member.proofType ? PROOF_LABEL[member.proofType] : ""} />
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-foreground-500">Proof number</dt>
            <dd className="tnum mt-1 flex items-center gap-2 text-sm text-foreground-900">
              <span className="tracking-wider">{member.proofNumber ? (revealProof ? member.proofNumber : maskProof(member.proofNumber)) : "—"}</span>
              {member.proofNumber ? (
                <button
                  type="button"
                  onClick={() => setRevealProof((v) => !v)}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700"
                  title={revealProof ? "Hide proof number" : "Show proof number"}
                >
                  <i className={revealProof ? "ri-eye-off-line text-base" : "ri-eye-line text-base"} />
                </button>
              ) : null}
            </dd>
            <p className="mt-1 text-xs text-foreground-400">Masked by default — sensitive personal data.</p>
          </div>
        </dl>
      </Card>

      <Card title="Bank details">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <InfoRow label="Account holder name" value={member.bankAccountName} />
          <InfoRow label="Account number" value={member.bankAccountNumber} />
          <InfoRow label="IFSC" value={member.ifsc} />
          <InfoRow label="UPI ID" value={member.upiId} />
        </dl>
      </Card>

      {member.notes ? (
        <Card title="Notes">
          <p className="whitespace-pre-wrap text-sm text-foreground-800">{member.notes}</p>
        </Card>
      ) : null}
    </div>
  );
}